import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Chip,
  Stack,
  Paper,
  Fade,
  Grow,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  IconButton,
  Switch,
  Divider,
  InputAdornment,
  Tooltip,
  Skeleton,
} from "@mui/material";
import {
  PhoneInTalk,
  Upload,
  Settings,
  Campaign,
  Schedule,
  Sms,
  AudioFile,
  ContactPhone,
  Check,
  Info,
  Warning,
  CheckCircle as SuccessIcon,
  Close,
  WhatsApp,
  Message,
  Tune,
  Preview,
  AccessTime,
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader, getToken } from "../../utils/util";

// ─── Time helpers ────────────────────────────────────────────────────────────
const parseTime = (timeStr) => {
  const [hours, minutes] = timeStr.split(":").map(Number);
  return { hours, minutes };
};

const toMinutes = (date) => date.getHours() * 60 + date.getMinutes();

/**
 * Returns true if `date` falls inside ANY of the blackout windows.
 * @param {Date} date
 * @param {Array<{start: string, end: string}>} nonActiveHours
 */
const isInBlackout = (date, nonActiveHours) => {
  const current = toMinutes(date);
  return nonActiveHours.some(({ start, end }) => {
    const s = parseTime(start);
    const e = parseTime(end);
    const startMins = s.hours * 60 + s.minutes;
    const endMins = e.hours * 60 + e.minutes;
    if (startMins > endMins) {
      // overnight window (e.g. 22:00 – 08:00)
      return current >= startMins || current < endMins;
    }
    return current >= startMins && current < endMins;
  });
};

/**
 * Find which blackout window (if any) the date currently falls in.
 * Returns the window object or null.
 */
const currentBlackoutWindow = (date, nonActiveHours) => {
  const current = toMinutes(date);
  return (
    nonActiveHours.find(({ start, end }) => {
      const s = parseTime(start);
      const e = parseTime(end);
      const startMins = s.hours * 60 + s.minutes;
      const endMins = e.hours * 60 + e.minutes;
      if (startMins > endMins) {
        return current >= startMins || current < endMins;
      }
      return current >= startMins && current < endMins;
    }) || null
  );
};

/**
 * Advance `date` forward until it lands on an active day/time slot.
 */
const advanceToActiveSlot = (date, activeDays, nonActiveHours) => {
  let result = new Date(date);
  const MAX_ITERS = 14 * 1440;

  for (let i = 0; i < MAX_ITERS; i++) {
    const dow = result.getDay();

    // Skip inactive days entirely
    if (!activeDays.includes(dow)) {
      result.setDate(result.getDate() + 1);
      result.setHours(0, 0, 0, 0);
      continue;
    }

    // Check if inside a blackout window
    const win = currentBlackoutWindow(result, nonActiveHours);
    if (win) {
      const e = parseTime(win.end);
      const blackoutEnd = new Date(result);
      blackoutEnd.setHours(e.hours, e.minutes, 0, 0);
      // If blackout end is in the past (overnight), push to next day
      if (blackoutEnd <= result) blackoutEnd.setDate(blackoutEnd.getDate() + 1);
      result.setTime(blackoutEnd.getTime());
      continue;
    }

    return result;
  }
  return result; // fallback – shouldn't hit this
};

/**
 * Generate an array of Date objects for each batch launch time.
 */
const generateBatchScheduleTimes = (baseTime, totalBatches, batchSettings) => {
  const { intervalMinutes, activeDays, nonActiveHours } = batchSettings;
  const times = [];
  let cursor = advanceToActiveSlot(
    new Date(baseTime),
    activeDays,
    nonActiveHours
  );
  times.push(new Date(cursor));

  for (let i = 1; i < totalBatches; i++) {
    cursor = new Date(cursor.getTime() + intervalMinutes * 60 * 1000);
    cursor = advanceToActiveSlot(cursor, activeDays, nonActiveHours);
    times.push(new Date(cursor));
  }
  return times;
};

const formatDateIST = (date) =>
  date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

// ─── Helpers ────────────────────────────────────────────────────────────────
const buildWaTemplate = (tpl) => ({
  templateId: tpl?.id || "",
  templateName: tpl?.name || "",
  templateLanguage: tpl?.language || "en_US",
});

const buildRcsTemplate = (tpl) => ({
  templateId: tpl?.templateId || "",
  templateName: tpl?.templateName || "",
});

const DTMF_INPUTS = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "0",
  "#",
  "*",
];
const WA_CONDITIONS = ["answered", "noagent"];

// ─── Empty fallback row ───────────────────────────────────────────────────────
const emptyDtmfRow = () => ({
  input: "1",
  type: "whatsapp",
  condition: "",
  templateId: "",
});

// ─── Default blackout window ─────────────────────────────────────────────────
const emptyBlackout = () => ({ start: "22:00", end: "08:00" });

// ─── Component ───────────────────────────────────────────────────────────────
const ImprovedVoiceCampaignForm = () => {
  // ── Existing data hooks ────────────────────────────────────────────────────
  const { data: voices, isLoading } = useApiEp(
    `/voice/voice-files-status/approved`
  );
  const { data: existingBaseFiles } = useApiEp(`/voice/existing-voice-clips`);

  const { data: rawWaTemplates } = useApiEp(`/api/get-templates-sync`);
  const { data: rcsTemplates } = useApiEp(`/rcs/get-templates?sq=`);

  const waTemplates = React.useMemo(() => {
    if (!rawWaTemplates) return [];
    return rawWaTemplates.data;
  }, [rawWaTemplates]);

  // ── Agent groups ───────────────────────────────────────────────────────────
  const [agentGroupsKey, setAgentGroupsKey] = useState(0);
  const [viewGroupDialogOpen, setViewGroupDialogOpen] = useState(false);
  const [viewingGroup, setViewingGroup] = useState(null);
  const { data: agentGroups, isLoading: loadingGroups } = useApiEp(
    `/voice/agent-groups?key=${agentGroupsKey}`
  );

  const [selectedAgentGroups, setSelectedAgentGroups] = useState([]);

  // ── Create group dialog ────────────────────────────────────────────────────
  const [createGroupDialog, setCreateGroupDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupAgents, setNewGroupAgents] = useState([
    { agentNumber: "", agentName: "" },
  ]);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  // ── Main form state ────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    templateId: "0",
    campaignName: "",
    location: "Bangalore",
    contactTab: "oldBaseFile",
    contactList: "",
    newContactsFile: null,
    baseName: "",
    oldBaseId: "",
    welcomePId: "",
    menuPId: "",
    noInputPId: "",
    wrongInputPId: "",
    thanksPId: "",
    dtmf: "",
    menuWaitTime: "5",
    rePrompt: "0",
    agentRows:
      '{"patchList":[{"groupId":"99","agentDtmf":"1","groupName":"Support"}]}',
    noAgentId: "102",
    retries: "",
    retryInterval: "",
    sendSmsSuccess: false,
    sendSmsFail: false,
    sendLater: false,
    sendWebhook: true,
    webhookId: "",
    scheduleTime: "",
  });

  // ── Fallback settings state ────────────────────────────────────────────────
  const [fallbackEnabled, setFallbackEnabled] = useState(false);
  const [rangeWa, setRangeWa] = useState({
    enabled: false,
    templateId: "",
    minDuration: "10",
    maxDuration: "60",
  });
  const [rangeRcs, setRangeRcs] = useState({
    enabled: false,
    templateId: "",
    minDuration: "10",
    maxDuration: "60",
  });

  // ── Batch settings — nonActiveHours is now an ARRAY of windows ────────────
  const [batchSettings, setBatchSettings] = useState({
    enabled: false,
    batchSize: "5000",
    interval: "60",
    activeDays: [1, 2, 3, 4, 5], // Mon–Fri
    nonActiveHours: [emptyBlackout()], // ← array of {start, end}
  });

  const [executiveSettings, setExecutiveSettings] = useState(false);
  const [dtmfRows, setDtmfRows] = useState([emptyDtmfRow()]);

  // ── Misc state ─────────────────────────────────────────────────────────────
  const [tabValue, setTabValue] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [baseFileId, setBaseFileId] = useState(null);
  const [isUploadingContacts, setIsUploadingContacts] = useState(false);
  const [saveToListDialog, setSaveToListDialog] = useState(false);
  const [pendingContacts, setPendingContacts] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [enabledPrompts, setEnabledPrompts] = useState({
    welcomePId: false,
    noInputPId: false,
    wrongInputPId: false,
    thanksPId: false,
  });
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    campaignId: "",
    message: "",
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("");

  // Contacts stored specifically for the batch submission after preview confirm
  const batchContactsRef = useRef([]);
  const [useContactsList, setUseContactsList] = useState(false);
  const { data: groups, isLoading: isLoadingGroups } = useApiEp(
    `/user/group-list`,
    useContactsList
  );
  const { data: schemaData, isLoading: isSchemaLoading } = useApiEp(
    `/user/get-contact-schema?groupName=${selectedGroup}`
  );

  // ── Contact tabs ───────────────────────────────────────────────────────────
  const contactTabs = useMemo(() => {
    if (batchSettings.enabled) {
      return [
        { label: "Manual Entry", value: 0 },
        { label: "Upload File", value: 1 },
      ];
    }
    return [
      { label: "Manual Entry", value: 0 },
      { label: "Upload File", value: 1 },
      { label: "Existing Files", value: 2 },
    ];
  }, [batchSettings.enabled]);

  // ── Validation helpers ─────────────────────────────────────────────────────
  const validatePhoneNumber = (n) =>
    /^\d{10}$/.test(n.replace(/\D/g, "").slice(-10)) &&
    n.replace(/\D/g, "").length === 10;

  const handleUseContactsListChange = (event) => {
    setUseContactsList(event.target.checked);
  };

  const extractPhoneNumbers = (text) => {
    const lines = text.split(/[\n,;]/);
    const numbers = [],
      errors = [];
    lines.forEach((line, i) => {
      const cleaned = line.trim().replace(/\D/g, "");
      if (cleaned)
        validatePhoneNumber(cleaned)
          ? numbers.push(cleaned)
          : errors.push(`Line ${i + 1}: Invalid "${line.trim()}"`);
    });
    return { numbers, errors };
  };

  const getGroupById = (id) =>
    agentGroups?.find((g) => String(g.groupId) === String(id));

  // ── Agent group creation ───────────────────────────────────────────────────
  const handleCreateGroup = async () => {
    const agents = newGroupAgents.map((a) => ({
      agentNumber: a.agentNumber.replace(/\D/g, "").slice(0, 10),
      agentName: a.agentName.trim() || a.agentNumber,
    }));
    setIsCreatingGroup(true);
    try {
      const res = await fetch(`${API_ENDPOINT}/voice/agent-groups`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ groupName: newGroupName.trim(), agents }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Creation failed");
      setAgentGroupsKey((p) => p + 1);
      setSnackbar({
        open: true,
        message: `Group "${newGroupName}" created`,
        severity: "success",
      });
      setCreateGroupDialog(false);
      setNewGroupName("");
      setNewGroupAgents([{ agentNumber: "", agentName: "" }]);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: "error" });
    } finally {
      setIsCreatingGroup(false);
    }
  };

  // ── Contact parsing ────────────────────────────────────────────────────────
  const parseCSVFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const lines = e.target.result.split(/\r?\n/);
          const numbers = [],
            errors = [];
          lines.forEach((line, idx) => {
            if (!line.trim()) return;
            let found = false;
            for (let col of line.split(/[,\t]/)) {
              const cleaned = col.trim().replace(/\D/g, "");
              if (cleaned && validatePhoneNumber(cleaned)) {
                numbers.push(cleaned);
                found = true;
                break;
              }
            }
            if (!found && line.trim())
              errors.push(`Line ${idx + 1}: No valid phone`);
          });
          resolve({ numbers, errors });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });

  const parseTXTFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(extractPhoneNumbers(e.target.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });

  const uploadContactsToAPI = async (contacts, saveToList) => {
    const res = await fetch(`${API_ENDPOINT}/voice/upload-contacts`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ contacts, saveToList }),
    });
    if (!res.ok) throw new Error("Upload failed");
    return (await res.json()).baseFileId;
  };

  const handleSaveToListConfirm = async (saveToList) => {
    setSaveToListDialog(false);
    setIsUploadingContacts(true);
    try {
      const id = await uploadContactsToAPI(pendingContacts, saveToList);
      setBaseFileId(id);
      setSnackbar({
        open: true,
        message: `Contacts uploaded! Base ID: ${id}`,
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to upload contacts",
        severity: "error",
      });
    } finally {
      setIsUploadingContacts(false);
      setPendingContacts([]);
    }
  };

  // ── Parse contacts (returns array of numbers) or null on failure ──────────
  const parseContacts = async () => {
    let contacts = [],
      errors = [];

    if (tabValue === 0) {
      if (!formData.contactList.trim()) {
        setValidationErrors(["Enter at least one contact"]);
        return null;
      }
      ({ numbers: contacts, errors } = extractPhoneNumbers(
        formData.contactList
      ));
    } else if (tabValue === 1) {
      if (!formData.newContactsFile) {
        setValidationErrors(["Select a file"]);
        return null;
      }
      if (!formData.baseName.trim()) {
        setValidationErrors(["Enter base file name"]);
        return null;
      }
      const ext = formData.newContactsFile.name.split(".").pop().toLowerCase();
      ({ numbers: contacts, errors } =
        ext === "csv"
          ? await parseCSVFile(formData.newContactsFile)
          : await parseTXTFile(formData.newContactsFile));
    }

    if (contacts.length > 1000000) {
      setValidationErrors([`Max 10 lakh contacts. Found: ${contacts.length}`]);
      return null;
    }
    const unique = [...new Set(contacts)];
    setValidationErrors(errors);
    return unique;
  };

  const validateAndPrepareContacts = async () => {
    try {
      if (tabValue === 2) {
        if (!formData.oldBaseId) {
          setValidationErrors(["Select existing file"]);
          return false;
        }
        setBaseFileId(formData.oldBaseId);
        return true;
      }
      const contacts = await parseContacts();
      if (!contacts) return false;
      setPendingContacts([...contacts]);
      setSaveToListDialog(true);
      return true;
    } catch {
      setSnackbar({
        open: true,
        message: "Error processing contacts",
        severity: "error",
      });
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData((p) => ({
      ...p,
      [name]:
        type === "checkbox" ? checked : type === "file" ? files[0] : value,
    }));
    if (validationErrors.length > 0) setValidationErrors([]);
    if (["contactList", "newContactsFile"].includes(name)) setBaseFileId(null);
  };

  const handleTabChange = (_, v) => {
    setTabValue(v);
    setFormData((p) => ({
      ...p,
      contactTab: ["contactList", "newBaseFile", "oldBaseFile"][v],
    }));
    setValidationErrors([]);
    setBaseFileId(null);
  };

  // ─── Blackout window helpers ────────────────────────────────────────────────
  const addBlackoutWindow = () =>
    setBatchSettings((p) => ({
      ...p,
      nonActiveHours: [...p.nonActiveHours, emptyBlackout()],
    }));

  const removeBlackoutWindow = (idx) =>
    setBatchSettings((p) => ({
      ...p,
      nonActiveHours: p.nonActiveHours.filter((_, i) => i !== idx),
    }));

  const updateBlackoutWindow = (idx, field, value) =>
    setBatchSettings((p) => {
      const updated = [...p.nonActiveHours];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...p, nonActiveHours: updated };
    });

  // ── Build fallback payload ─────────────────────────────────────────────────
  const buildFallbackPayload = () => {
    if (!fallbackEnabled) return {};
    const findWa = (id) => waTemplates.find((t) => t.id === id);
    const findRcs = (id) =>
      (rcsTemplates || []).find((t) => t.templateId === id);
    const payload = {};

    if (rangeWa.enabled && rangeWa.templateId) {
      const tpl = findWa(rangeWa.templateId);
      payload.rangeSettingsWhatsapp = {
        template: tpl
          ? buildWaTemplate(tpl)
          : { templateId: rangeWa.templateId },
        minDuration: Number(rangeWa.minDuration),
        maxDuration: Number(rangeWa.maxDuration),
      };
    }

    if (rangeRcs.enabled && rangeRcs.templateId) {
      const tpl = findRcs(rangeRcs.templateId);
      payload.rangeSettingsRcs = {
        template: tpl
          ? buildRcsTemplate(tpl)
          : { templateId: rangeRcs.templateId },
        minDuration: Number(rangeRcs.minDuration),
        maxDuration: Number(rangeRcs.maxDuration),
      };
    }

    const dtmfSettings = dtmfRows
      .filter((r) => r.input && r.templateId)
      .map((r) => {
        let template = {};
        if (r.type === "whatsapp") {
          const tpl = findWa(r.templateId);
          template = tpl ? buildWaTemplate(tpl) : { templateId: r.templateId };
        } else {
          const tpl = findRcs(r.templateId);
          template = tpl ? buildRcsTemplate(tpl) : { templateId: r.templateId };
        }
        return {
          input: r.input,
          type: r.type,
          ...(r.condition ? { condition: r.condition } : {}),
          template,
        };
      });

    if (dtmfSettings.length > 0) payload.dtmfSettings = dtmfSettings;
    return payload;
  };

  const buildBatchPayload = () => {
    if (!batchSettings.enabled) return {};
    return {
      enabled: true,
      batchSize: Number(batchSettings.batchSize),
      intervalMinutes: Number(batchSettings.interval),
      activeDays: batchSettings.activeDays,
      nonActiveHours: batchSettings.nonActiveHours, // array of {start, end}
    };
  };

  // ── Build batch preview data ────────────────────────────────────────────────
  const buildBatchPreview = (contacts) => {
    const batchSize = Math.max(1, Number(batchSettings.batchSize));
    const totalBatches = Math.ceil(contacts.length / batchSize);
    const scheduleTimes = generateBatchScheduleTimes(new Date(), totalBatches, {
      intervalMinutes: Number(batchSettings.interval),
      activeDays: batchSettings.activeDays,
      nonActiveHours: batchSettings.nonActiveHours,
    });

    const schedules = scheduleTimes.map((t, i) => ({
      batch: i + 1,
      contacts: Math.min(batchSize, contacts.length - i * batchSize),
      scheduledAt: formatDateIST(t),
      rawDate: t,
    }));

    return {
      totalContacts: contacts.length,
      totalBatches,
      batchSize,
      intervalMinutes: Number(batchSettings.interval),
      schedules,
    };
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  /**
   * @param {string|null} passedBaseFileId  – used for the non-batch path
   * @param {string[]|null} contactsArray   – used for the batch path (sent inline)
   */
  const handleActualSubmit = async (passedBaseFileId, contactsArray = null) => {
    setIsSubmitting(true);
    const effectiveBaseFileId = passedBaseFileId ?? baseFileId;
    try {
      let agentRowsPayload = "";
      if (formData.templateId === "2") {
        agentRowsPayload =
          selectedAgentGroups.length > 0
            ? JSON.stringify({
                patchList: selectedAgentGroups.map((g) => ({
                  groupId: g.groupId,
                  agentDtmf: g.dtmf,
                  groupName: g.groupName,
                })),
              })
            : '""';
      } else {
        agentRowsPayload = formData.agentRows;
      }

      const payload = {
        campaignName: formData.campaignName,
        templateId: formData.templateId,
        // When batch mode: pass contacts inline, no baseFileId needed
        ...(batchSettings.enabled
          ? { contacts: contactsArray }
          : { baseFileId: effectiveBaseFileId }),
        welcomePId: formData.welcomePId,
        menuPId: formData.menuPId,
        noInputPId: formData.noInputPId,
        wrongInputPId: formData.wrongInputPId,
        thanksPId: formData.thanksPId,
        dtmf: formData.dtmf,
        menuWaitTime: formData.menuWaitTime,
        rePrompt: formData.rePrompt,
        agentRows: agentRowsPayload,
        noAgentId: formData.noAgentId,
        sendSmsSuccess: formData.sendSmsSuccess,
        sendSmsFail: formData.sendSmsFail,
        sendSmsDtmf: false,
        sendWebhook: formData.sendWebhook,
        webhookId: formData.webhookId,
        sendLater: formData.sendLater,
        scheduleTime: formData.scheduleTime,
        retries: formData.retries,
        retryInterval: formData.retryInterval,
        location: formData.location,
        settings: buildFallbackPayload(),
        batchSettings: buildBatchPayload(),
        executiveSettings,
      };

      const res = await fetch(`${API_ENDPOINT}/voice/campaign/compose`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setSuccessDialog({
          open: true,
          campaignId: data.campaignId,
          message: data.message || "Campaign launched!",
        });
      } else throw new Error(data.message || "Launch failed");
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.message || "Failed to launch",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (baseFileId && pendingContacts.length === 0 && tabValue !== 2)
      handleActualSubmit();
  }, [baseFileId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ── BATCH MODE ─────────────────────────────────────────────────────────
    if (batchSettings.enabled) {
      try {
        const contacts = await parseContacts();
        if (!contacts || contacts.length === 0) {
          setSnackbar({
            open: true,
            message: "No valid contacts found",
            severity: "error",
          });
          return;
        }
        if (batchSettings.activeDays.length === 0) {
          setSnackbar({
            open: true,
            message: "Select at least one active day",
            severity: "error",
          });
          return;
        }
        // Build preview
        const preview = buildBatchPreview(contacts);
        batchContactsRef.current = contacts;
        setPreviewData(preview);
        setPreviewOpen(true);
      } catch {
        setSnackbar({
          open: true,
          message: "Error processing contacts",
          severity: "error",
        });
      }
      return;
    }

    // ── NORMAL MODE ────────────────────────────────────────────────────────
    if (tabValue === 2) {
      if (!formData.oldBaseId) {
        setSnackbar({
          open: true,
          message: "Please select existing contact file",
          severity: "error",
        });
        return;
      }
      await handleActualSubmit(formData.oldBaseId);
      return;
    }
    if (!baseFileId) {
      await validateAndPrepareContacts();
      return;
    }
    await handleActualSubmit();
  };

  // Called when user clicks "Confirm & Launch" inside the preview dialog
  const handlePreviewConfirm = async () => {
    setPreviewOpen(false);
    await handleActualSubmit(null, batchContactsRef.current);
  };

  // ── DTMF row helpers ───────────────────────────────────────────────────────
  const addDtmfRow = () => setDtmfRows([...dtmfRows, emptyDtmfRow()]);
  const removeDtmfRow = (i) =>
    setDtmfRows(dtmfRows.filter((_, idx) => idx !== i));
  const updateDtmfRow = (i, field, value) => {
    const rows = [...dtmfRows];
    rows[i] = { ...rows[i], [field]: value };
    setDtmfRows(rows);
  };

  // ── Static options ─────────────────────────────────────────────────────────
  const oldBaseOptions = [
    { value: "", label: "Select File" },
    ...(existingBaseFiles || []).map((item) => ({
      value: item.baseId,
      label: item.baseName || item.baseId,
    })),
  ];
  const webhookOptions = [
    { value: "", label: "Select Webhook" },
    { value: "301", label: "Voice Hangup" },
  ];

  if (isLoading) return <MuPb />;

  const sectionHeaderSx = {
    p: 3,
    background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
    borderBottom: "1px solid #e5e7eb",
  };

  // ── Day label helper ───────────────────────────────────────────────────────
  const DAY_LABELS = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f3f4f6 0%, #ffffff 100%)",
        py: 6,
        px: 2,
      }}
    >
      <Box sx={{ maxWidth: 1000, mx: "auto" }}>
        {/* Header */}
        <Fade in timeout={800}>
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <PhoneInTalk sx={{ fontSize: 48, color: "#3b82f6", mb: 2 }} />
            <Typography
              variant="h3"
              fontWeight={700}
              sx={{
                fontFamily: "'Sora', sans-serif",
                letterSpacing: "-0.02em",
              }}
            >
              Voice Campaign Studio
            </Typography>
          </Box>
        </Fade>

        {/* Info Alert */}
        <Grow in timeout={1000}>
          <Paper
            sx={{
              p: 3,
              mb: 4,
              background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
              border: "1px solid rgba(59,130,246,0.3)",
            }}
          >
            <Stack direction="row" spacing={2}>
              <Info sx={{ color: "#3b82f6", fontSize: 28 }} />
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Contact Guidelines
                </Typography>
                <Typography>
                  Max 10 lakh records • 10-digit numbers only
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grow>

        {baseFileId && !batchSettings.enabled && (
          <Alert severity="success" sx={{ mb: 4 }}>
            Contacts ready! Base File ID: <strong>{baseFileId}</strong>
          </Alert>
        )}
        {validationErrors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 4 }}>
            {validationErrors.map((e, i) => (
              <div key={i}>• {e}</div>
            ))}
          </Alert>
        )}

        <Grow in timeout={1200}>
          <Card
            sx={{ borderRadius: 4, boxShadow: "0 20px 60px rgba(0,0,0,0.05)" }}
          >
            <Box
              sx={{
                p: 4,
                background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Campaign sx={{ color: "#3b82f6", fontSize: 32 }} />
                <Typography variant="h5" fontWeight={600}>
                  Campaign Configuration
                </Typography>
              </Stack>
            </Box>

            <CardContent sx={{ p: 4 }}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={4}>
                  {/* ── Template ──────────────────────────────────────────── */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Stack direction="row" spacing={1.5} mb={3}>
                        <Settings sx={{ color: "#3b82f6" }} />
                        <Typography fontWeight={600}>IVR Template</Typography>
                      </Stack>
                      <RadioGroup
                        row
                        value={formData.templateId}
                        onChange={handleChange}
                        name="templateId"
                        sx={{ gap: 2 }}
                      >
                        {[
                          { value: "0", label: "Simple IVR", icon: "📞" },
                          { value: "1", label: "DTMF Input", icon: "⌨️" },
                          { value: "2", label: "Call Patch", icon: "🔄" },
                        ].map((opt) => (
                          <Paper
                            key={opt.value}
                            sx={{
                              flex: 1,
                              p: 3,
                              cursor: "pointer",
                              border:
                                formData.templateId === opt.value
                                  ? "2px solid #3b82f6"
                                  : "1px solid #e5e7eb",
                              background:
                                formData.templateId === opt.value
                                  ? "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)"
                                  : "#f8fafc",
                              color:
                                formData.templateId === opt.value
                                  ? "#fff"
                                  : "#1f2937",
                            }}
                            onClick={() =>
                              handleChange({
                                target: {
                                  name: "templateId",
                                  value: opt.value,
                                },
                              })
                            }
                          >
                            <FormControlLabel
                              value={opt.value}
                              control={
                                <Radio
                                  sx={{
                                    color:
                                      formData.templateId === opt.value
                                        ? "#fff"
                                        : "#9ca3af",
                                  }}
                                />
                              }
                              label={
                                <Box textAlign="center">
                                  <Typography fontSize="2.5rem" mb={1}>
                                    {opt.icon}
                                  </Typography>
                                  <Typography fontWeight={600}>
                                    {opt.label}
                                  </Typography>
                                </Box>
                              }
                              sx={{ m: 0, width: "100%" }}
                            />
                          </Paper>
                        ))}
                      </RadioGroup>
                    </Paper>
                  </Grid>

                  {/* ── Campaign Details ───────────────────────────────────── */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      name="campaignName"
                      label="Campaign Name"
                      value={formData.campaignName}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Location</InputLabel>
                      <Select
                        label="Location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                      >
                        <MenuItem value="Bangalore">Bangalore</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* ══════════════════════════════════════════════════════════
                      BATCH SETTINGS
                  ══════════════════════════════════════════════════════════ */}
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        overflow: "hidden",
                        border: batchSettings.enabled
                          ? "1px solid rgba(59,130,246,0.4)"
                          : "1px solid #e5e7eb",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ ...sectionHeaderSx, cursor: "pointer" }}
                        onClick={() =>
                          setBatchSettings((p) => ({
                            ...p,
                            enabled: !p.enabled,
                          }))
                        }
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Schedule sx={{ color: "#3b82f6" }} />
                          <Box>
                            <Typography fontWeight={600}>
                              Batching Settings
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Split campaign into batches with interval and
                              daily schedule
                            </Typography>
                          </Box>
                        </Stack>
                        <Switch
                          checked={batchSettings.enabled}
                          onChange={(e) => {
                            e.stopPropagation();
                            setBatchSettings((p) => ({
                              ...p,
                              enabled: e.target.checked,
                            }));
                          }}
                          color="primary"
                        />
                      </Stack>

                      {batchSettings.enabled && (
                        <Box sx={{ p: 3 }}>
                          {/* Batch size + interval */}
                          <Grid container spacing={3} mb={4}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Batch Size"
                                type="number"
                                size="small"
                                value={batchSettings.batchSize}
                                onChange={(e) =>
                                  setBatchSettings((p) => ({
                                    ...p,
                                    batchSize: e.target.value,
                                  }))
                                }
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      contacts
                                    </InputAdornment>
                                  ),
                                }}
                                helperText="Contacts per batch (recommended ≤ 10,000)"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Interval Between Batches"
                                type="number"
                                size="small"
                                value={batchSettings.interval}
                                onChange={(e) =>
                                  setBatchSettings((p) => ({
                                    ...p,
                                    interval: e.target.value,
                                  }))
                                }
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      minutes
                                    </InputAdornment>
                                  ),
                                }}
                                helperText="Delay before next batch starts"
                              />
                            </Grid>
                          </Grid>

                          {/* Active days */}
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            mb={1}
                          >
                            Active Days of the Week
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={2}
                          >
                            Batches will only run on selected days
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 2,
                              mb: 4,
                            }}
                          >
                            {DAY_LABELS.map((day, idx) => (
                              <FormControlLabel
                                key={idx}
                                control={
                                  <Checkbox
                                    checked={batchSettings.activeDays.includes(
                                      idx
                                    )}
                                    onChange={(e) => {
                                      setBatchSettings((p) => ({
                                        ...p,
                                        activeDays: e.target.checked
                                          ? [...p.activeDays, idx]
                                          : p.activeDays.filter(
                                              (d) => d !== idx
                                            ),
                                      }));
                                    }}
                                    size="small"
                                  />
                                }
                                label={day}
                              />
                            ))}
                          </Box>

                          <Divider sx={{ my: 3 }} />

                          {/* ── MULTIPLE BLACKOUT PERIODS ─────────────────────── */}
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                          >
                            <Box>
                              <Typography variant="subtitle1" fontWeight={600}>
                                Daily Non-Active Hours (Blackout)
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No batches will run during these windows. You
                                can add multiple periods.
                              </Typography>
                            </Box>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={addBlackoutWindow}
                            >
                              Add Window
                            </Button>
                          </Stack>

                          <Stack spacing={2} mt={2}>
                            {batchSettings.nonActiveHours.map((win, idx) => (
                              <Paper
                                key={idx}
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: "#fafafa",
                                  borderColor: "#e5e7eb",
                                  position: "relative",
                                }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  alignItems="center"
                                  flexWrap="wrap"
                                >
                                  <AccessTime
                                    sx={{ color: "#6b7280", fontSize: 20 }}
                                  />
                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    sx={{ minWidth: 60 }}
                                  >
                                    Window {idx + 1}
                                  </Typography>
                                  <TextField
                                    label="From"
                                    type="time"
                                    size="small"
                                    value={win.start}
                                    onChange={(e) =>
                                      updateBlackoutWindow(
                                        idx,
                                        "start",
                                        e.target.value
                                      )
                                    }
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ width: 150 }}
                                  />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    to
                                  </Typography>
                                  <TextField
                                    label="To"
                                    type="time"
                                    size="small"
                                    value={win.end}
                                    onChange={(e) =>
                                      updateBlackoutWindow(
                                        idx,
                                        "end",
                                        e.target.value
                                      )
                                    }
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ width: 150 }}
                                  />
                                  <Chip
                                    size="small"
                                    label={
                                      win.start > win.end
                                        ? "Overnight"
                                        : "Same day"
                                    }
                                    color={
                                      win.start > win.end
                                        ? "warning"
                                        : "default"
                                    }
                                    variant="outlined"
                                  />
                                  <Tooltip title="Remove this blackout window">
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                          removeBlackoutWindow(idx)
                                        }
                                        disabled={
                                          batchSettings.nonActiveHours
                                            .length === 1
                                        }
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              </Paper>
                            ))}
                          </Stack>

                          <Alert severity="info" sx={{ mt: 2 }}>
                            Example: <strong>22:00 – 08:00</strong> = no
                            activity overnight • <strong>13:00 – 14:00</strong>{" "}
                            = no activity during lunch
                          </Alert>

                          {/* Preview CTA */}
                          <Box sx={{ mt: 3 }}>
                            <Alert severity="success" icon={<Preview />}>
                              When you launch, a <strong>batch preview</strong>{" "}
                              will be shown before the campaign starts —
                              including exact send times for each batch.
                            </Alert>
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* ── Contact Upload ─────────────────────────────────────── */}
                  <Grid item xs={12}>
                    <Paper sx={{ overflow: "hidden" }}>
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ p: 3, background: "#f8fafc" }}
                      >
                        <ContactPhone sx={{ color: "#3b82f6" }} />
                        <Box>
                          <Typography fontWeight={600}>Use Contacts</Typography>

                          {/* <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={useContactsList}
                                onChange={handleUseContactsListChange}
                              />
                            }
                            label="Use contacts List"
                          /> */}
                          {batchSettings.enabled && (
                            <Typography variant="caption" color="primary">
                              Batch mode: contacts will be sent inline (no
                              separate upload)
                            </Typography>
                          )}
                        </Box>
                      </Stack>

                      {!useContactsList ? (
                        <Box>
                          <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            sx={{ px: 3 }}
                          >
                            {contactTabs.map((tab) => (
                              <Tab key={tab.value} label={tab.label} />
                            ))}
                          </Tabs>
                          <Box sx={{ p: 3 }}>
                            {tabValue === 0 && (
                              <TextField
                                fullWidth
                                multiline
                                rows={6}
                                name="contactList"
                                placeholder={"9876543210\n9123456789"}
                                value={formData.contactList}
                                onChange={handleChange}
                              />
                            )}
                            {tabValue === 1 && (
                              <Stack spacing={3}>
                                <Box
                                  sx={{
                                    p: 6,
                                    border: "2px dashed #cbd5e1",
                                    textAlign: "center",
                                    borderRadius: 2,
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    document
                                      .getElementById("file-upload")
                                      .click()
                                  }
                                >
                                  <Upload
                                    sx={{
                                      fontSize: 60,
                                      color: "#3b82f6",
                                      mb: 2,
                                    }}
                                  />
                                  <input
                                    type="file"
                                    id="file-upload"
                                    name="newContactsFile"
                                    onChange={handleChange}
                                    accept=".csv,.txt"
                                    style={{ display: "none" }}
                                  />
                                  <Button variant="outlined" component="span">
                                    Choose .csv or .txt
                                  </Button>
                                  <Typography mt={2}>
                                    {formData.newContactsFile
                                      ? formData.newContactsFile.name
                                      : "10-digit numbers only"}
                                  </Typography>
                                </Box>
                                <TextField
                                  fullWidth
                                  required
                                  name="baseName"
                                  label="Base File Name"
                                  value={formData.baseName}
                                  onChange={handleChange}
                                />
                              </Stack>
                            )}
                            {tabValue === 2 && (
                              <FormControl fullWidth>
                                <InputLabel>
                                  Select Existing Contact File
                                </InputLabel>
                                <Select
                                  label="Select Existing Contact File"
                                  name="oldBaseId"
                                  value={formData.oldBaseId}
                                  onChange={handleChange}
                                  required
                                >
                                  {oldBaseOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ p: 2 }} maxWidth={500}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="group-select-label">
                              Select Group
                            </InputLabel>

                            <Select
                              labelId="group-select-label"
                              value={selectedGroup}
                              label="Select Contact List"
                              onChange={(e) => setSelectedGroup(e.target.value)}
                              disabled={isLoadingGroups}
                            >
                              {(groups || []).map((group) => (
                                <MenuItem key={group} value={group}>
                                  {group}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {isSchemaLoading ? (
                            <Box sx={{ p: 2, display: "flex", gap: 1 }}>
                              {[...Array(4)].map((_, i) => (
                                <Skeleton
                                  key={i}
                                  variant="rounded"
                                  width={80}
                                  height={24}
                                />
                              ))}
                            </Box>
                          ) : (
                            schemaData && (
                              <Box sx={{ p: 2 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mb: 1, display: "block" }}
                                >
                                  Available Fields
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                  }}
                                >
                                  {Object.keys(schemaData)
                                    .filter(
                                      (key) =>
                                        ![
                                          "_id",
                                          "groupName",
                                          "createdBy",
                                          "customerId",
                                          "createdOn",
                                          "updatedOn",
                                        ].includes(key)
                                    )
                                    .map((key) => (
                                      <Chip
                                        key={key}
                                        label={key}
                                        size="small"
                                        variant="outlined"
                                      />
                                    ))}
                                </Box>
                              </Box>
                            )
                          )}
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* ── Audio Prompts ──────────────────────────────────────── */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3, height: "100%" }}>
                      <Stack direction="row" spacing={1.5} mb={2.5}>
                        <AudioFile sx={{ color: "#3b82f6" }} />
                        <Typography fontWeight={600}>Voice Prompt</Typography>
                      </Stack>

                      {formData.templateId === "0" && (
                        <FormControl fullWidth>
                          <InputLabel>Welcome Prompt</InputLabel>
                          <Select
                            label="Welcome Prompt"
                            name="welcomePId"
                            value={formData.welcomePId}
                            onChange={handleChange}
                            required
                          >
                            {voices?.map((v) => (
                              <MenuItem
                                key={v.voiceFileId}
                                value={v.voiceFileId}
                              >
                                {v.originalFileName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      {(formData.templateId === "1" ||
                        formData.templateId === "2") && (
                        <>
                          <Grid container spacing={2} mb={3}>
                            <Grid item xs={12} md={4}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Menu Prompt</InputLabel>
                                <Select
                                  label="Menu Prompt"
                                  name="menuPId"
                                  value={formData.menuPId}
                                  onChange={handleChange}
                                  required
                                >
                                  {voices
                                    ?.filter(
                                      (item) => item.promptCategory === "menu"
                                    )
                                    .map((v) => (
                                      <MenuItem
                                        key={v.voiceFileId}
                                        value={v.voiceFileId}
                                      >
                                        {v.fileName}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Menu Wait Time</InputLabel>
                                <Select
                                  label="Menu Wait Time"
                                  name="menuWaitTime"
                                  value={formData.menuWaitTime}
                                  onChange={handleChange}
                                >
                                  {[5, 10, 15, 20, 30].map((s) => (
                                    <MenuItem key={s} value={s.toString()}>
                                      {s} sec
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>RePrompt</InputLabel>
                                <Select
                                  label="RePrompt"
                                  name="rePrompt"
                                  value={formData.rePrompt}
                                  onChange={handleChange}
                                >
                                  {[0, 1, 2, 3].map((num) => (
                                    <MenuItem key={num} value={num.toString()}>
                                      {num}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          </Grid>

                          <Divider sx={{ mb: 2 }} />
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            Optional Prompts
                          </Typography>

                          <Grid container spacing={2}>
                            {[
                              {
                                label: "Welcome Prompt",
                                name: "welcomePId",
                                category: "welcome",
                              },
                              {
                                label: "No Input Prompt",
                                name: "noInputPId",
                                category: "noinput",
                              },
                              {
                                label: "Wrong Input Prompt",
                                name: "wrongInputPId",
                                category: "wronginput",
                              },
                              {
                                label: "Thanks Prompt",
                                name: "thanksPId",
                                category: "thanks",
                              },
                            ].map((item) => (
                              <Grid item xs={12} md={6} key={item.name}>
                                <Box
                                  sx={{
                                    border: "1px solid",
                                    borderColor: enabledPrompts[item.name]
                                      ? "primary.light"
                                      : "divider",
                                    borderRadius: 2,
                                    p: 1.5,
                                    transition: "border-color 0.2s",
                                  }}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={enabledPrompts[item.name]}
                                        onChange={(e) => {
                                          setEnabledPrompts((prev) => ({
                                            ...prev,
                                            [item.name]: e.target.checked,
                                          }));
                                          if (!e.target.checked)
                                            setFormData((prev) => ({
                                              ...prev,
                                              [item.name]: "",
                                            }));
                                        }}
                                      />
                                    }
                                    label={
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                      >
                                        {item.label}
                                      </Typography>
                                    }
                                  />
                                  {enabledPrompts[item.name] && (
                                    <Select
                                      name={item.name}
                                      value={formData[item.name]}
                                      onChange={handleChange}
                                      displayEmpty
                                      fullWidth
                                      size="small"
                                      sx={{ mt: 1 }}
                                    >
                                      <MenuItem value="">
                                        <em>Select File</em>
                                      </MenuItem>
                                      {voices
                                        ?.filter(
                                          (v) =>
                                            v.promptCategory === item.category
                                        )
                                        .map((v) => (
                                          <MenuItem
                                            key={v.voiceFileId}
                                            value={v.voiceFileId}
                                          >
                                            {v.fileName}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  )}
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </>
                      )}
                    </Paper>
                  </Grid>

                  {/* ── Advanced Options ───────────────────────────────────── */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Stack direction="row" spacing={1.5} mb={3}>
                        <Settings sx={{ color: "#3b82f6" }} />
                        <Typography fontWeight={600}>
                          Advanced Options
                        </Typography>
                      </Stack>

                      {formData.templateId === "1" && (
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              select
                              size="small"
                              fullWidth
                              label="DTMF input"
                              name="dtmf"
                              value={formData.dtmf}
                              onChange={handleChange}
                            >
                              {[...Array(10).keys()].map((num) => (
                                <MenuItem key={num} value={num}>
                                  {num}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </Grid>
                      )}

                      {formData.templateId === "2" && (
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                              Agent Groups & DTMF Mapping
                            </Typography>
                            {loadingGroups ? (
                              <CircularProgress size={24} />
                            ) : (
                              <Stack spacing={2}>
                                {selectedAgentGroups.map((group, index) => (
                                  <Paper
                                    key={index}
                                    sx={{ p: 2, bgcolor: "#fafafa" }}
                                  >
                                    <Grid
                                      container
                                      spacing={2}
                                      alignItems="center"
                                    >
                                      <Grid item xs={5}>
                                        <FormControl fullWidth size="small">
                                          <InputLabel>Agent Group</InputLabel>
                                          <Select
                                            label="Agent Group"
                                            value={group.groupId || ""}
                                            onChange={(e) => {
                                              const ng = [
                                                ...selectedAgentGroups,
                                              ];
                                              const sel = getGroupById(
                                                e.target.value
                                              );
                                              ng[index] = {
                                                ...ng[index],
                                                groupId: e.target.value,
                                                groupName: sel?.groupName || "",
                                              };
                                              setSelectedAgentGroups(ng);
                                            }}
                                          >
                                            {agentGroups?.map((g) => (
                                              <MenuItem
                                                key={g.groupId}
                                                value={g.groupId}
                                              >
                                                {g.groupName} (
                                                {g.agents?.length || 0} agents)
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </Grid>
                                      <Grid item xs={1}>
                                        <IconButton
                                          color="primary"
                                          size="small"
                                          onClick={() => {
                                            const gd = getGroupById(
                                              group.groupId
                                            );
                                            if (gd) {
                                              setViewingGroup(gd);
                                              setViewGroupDialogOpen(true);
                                            }
                                          }}
                                          disabled={!group.groupId}
                                        >
                                          <Info />
                                        </IconButton>
                                      </Grid>
                                      <Grid item xs={4}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label="DTMF Digit"
                                          value={group.dtmf || ""}
                                          onChange={(e) => {
                                            const ng = [...selectedAgentGroups];
                                            ng[index].dtmf = e.target.value;
                                            setSelectedAgentGroups(ng);
                                          }}
                                          placeholder="e.g., 1"
                                        />
                                      </Grid>
                                      <Grid item xs={2}>
                                        <IconButton
                                          color="error"
                                          onClick={() =>
                                            setSelectedAgentGroups(
                                              selectedAgentGroups.filter(
                                                (_, i) => i !== index
                                              )
                                            )
                                          }
                                        >
                                          <Close />
                                        </IconButton>
                                      </Grid>
                                    </Grid>
                                  </Paper>
                                ))}
                                <Stack direction="row" spacing={2}>
                                  <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() =>
                                      setSelectedAgentGroups([
                                        ...selectedAgentGroups,
                                        {
                                          groupId: "",
                                          groupName: "",
                                          dtmf: "",
                                        },
                                      ])
                                    }
                                  >
                                    Add Agent Group
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => setCreateGroupDialog(true)}
                                  >
                                    Create New Group
                                  </Button>
                                </Stack>
                              </Stack>
                            )}
                          </Grid>
                        </Grid>
                      )}

                      {formData.templateId === "0" && (
                        <Typography color="text.secondary">
                          Simple IVR — no extra settings needed
                        </Typography>
                      )}
                    </Paper>
                  </Grid>

                  {/* ── Retry Settings ─────────────────────────────────────── */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Stack direction="row" spacing={1.5} mb={2.5}>
                        <Schedule sx={{ color: "#3b82f6" }} />
                        <Typography fontWeight={600}>Retry Settings</Typography>
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Attempts</InputLabel>
                            <Select
                              label="Attempts"
                              name="retries"
                              value={formData.retries}
                              onChange={handleChange}
                            >
                              {[0, 1, 2, 3].map((n) => (
                                <MenuItem key={n} value={n.toString()}>
                                  {n}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Interval</InputLabel>
                            <Select
                              label="Interval"
                              name="retryInterval"
                              value={formData.retryInterval}
                              onChange={handleChange}
                            >
                              <MenuItem value="900">15 min</MenuItem>
                              <MenuItem value="1800">30 min</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* ── Notifications ──────────────────────────────────────── */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Stack direction="row" spacing={1.5} mb={3}>
                        <Sms sx={{ color: "#3b82f6" }} />
                        <Typography fontWeight={600}>Notifications</Typography>
                      </Stack>
                      <Grid container spacing={3}>
                        {[
                          ["sendSmsSuccess", "SMS Success"],
                          ["sendSmsFail", "SMS Failure"],
                          ["sendLater", "Schedule"],
                          ["sendWebhook", "Webhook"],
                        ].map(([name, label]) => (
                          <Grid item xs={12} sm={6} md={3} key={name}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name={name}
                                  checked={formData[name]}
                                  onChange={handleChange}
                                />
                              }
                              label={label}
                            />
                          </Grid>
                        ))}
                        {formData.sendLater && (
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              type="datetime-local"
                              name="scheduleTime"
                              label="Schedule Time"
                              value={formData.scheduleTime}
                              onChange={handleChange}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        )}
                        {formData.sendWebhook && (
                          <Grid item xs={12}>
                            <FormControl fullWidth>
                              <InputLabel>Webhook</InputLabel>
                              <Select
                                label="Webhook"
                                name="webhookId"
                                value={formData.webhookId}
                                onChange={handleChange}
                                required
                              >
                                {webhookOptions.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* ── Executive Notifications ───────────────────────────── */}
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        overflow: "hidden",
                        border: executiveSettings
                          ? "1px solid rgba(59,130,246,0.4)"
                          : "1px solid #e5e7eb",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ ...sectionHeaderSx, cursor: "pointer" }}
                        onClick={() => setExecutiveSettings((prev) => !prev)}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Schedule sx={{ color: "#3b82f6" }} />
                          <Box>
                            <Typography fontWeight={600}>
                              Enable Executive Notifications
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Executive will be notified of the customers to
                              call back
                            </Typography>
                          </Box>
                        </Stack>
                        <Switch
                          checked={executiveSettings}
                          onChange={(e) => {
                            e.stopPropagation();
                            setExecutiveSettings(e.target.checked);
                          }}
                          color="primary"
                        />
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* ══════════════════════════════════════════════════════════
                      FALLBACK MESSAGE SETTINGS
                  ══════════════════════════════════════════════════════════ */}
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        overflow: "hidden",
                        border: fallbackEnabled
                          ? "1px solid rgba(59,130,246,0.4)"
                          : "1px solid #e5e7eb",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ ...sectionHeaderSx, cursor: "pointer" }}
                        onClick={() => setFallbackEnabled((v) => !v)}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Tune sx={{ color: "#3b82f6" }} />
                          <Box>
                            <Typography fontWeight={600}>
                              Fallback Message Settings
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Send WhatsApp / RCS messages based on call
                              duration or DTMF input
                            </Typography>
                          </Box>
                        </Stack>
                        <Switch
                          checked={fallbackEnabled}
                          onChange={(e) => {
                            e.stopPropagation();
                            setFallbackEnabled(e.target.checked);
                          }}
                          color="primary"
                        />
                      </Stack>

                      {fallbackEnabled && (
                        <Box sx={{ p: 3 }}>
                          {/* Duration-Based */}
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            mb={2}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Schedule
                              fontSize="small"
                              sx={{ color: "#6366f1" }}
                            />{" "}
                            Duration-Based Fallback
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={3}
                          >
                            Send a message when call duration falls within a
                            specified range (seconds).
                          </Typography>

                          <Grid container spacing={3} mb={4}>
                            {/* WhatsApp range */}
                            <Grid item xs={12}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2.5,
                                  borderRadius: 2,
                                  bgcolor: rangeWa.enabled
                                    ? "#f0f9ff"
                                    : "#fafafa",
                                }}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={2}
                                  mb={rangeWa.enabled ? 2 : 0}
                                >
                                  <WhatsApp
                                    sx={{ color: "#25d366", fontSize: 22 }}
                                  />
                                  <Typography fontWeight={500} sx={{ flex: 1 }}>
                                    WhatsApp Fallback
                                  </Typography>
                                  <Switch
                                    size="small"
                                    checked={rangeWa.enabled}
                                    onChange={(e) =>
                                      setRangeWa((p) => ({
                                        ...p,
                                        enabled: e.target.checked,
                                      }))
                                    }
                                  />
                                </Stack>
                                {rangeWa.enabled && (
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>Template</InputLabel>
                                        <Select
                                          label="Template"
                                          value={rangeWa.templateId}
                                          onChange={(e) =>
                                            setRangeWa((p) => ({
                                              ...p,
                                              templateId: e.target.value,
                                            }))
                                          }
                                        >
                                          <MenuItem value="">
                                            <em>Select template</em>
                                          </MenuItem>
                                          {waTemplates.map((t) => (
                                            <MenuItem key={t.id} value={t.id}>
                                              <Stack>
                                                <Typography
                                                  variant="body2"
                                                  fontWeight={500}
                                                >
                                                  {t.name}
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  {t.language}
                                                </Typography>
                                              </Stack>
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    <Grid item xs={6} md={4}>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        label="Min Duration (sec)"
                                        type="number"
                                        value={rangeWa.minDuration}
                                        onChange={(e) =>
                                          setRangeWa((p) => ({
                                            ...p,
                                            minDuration: e.target.value,
                                          }))
                                        }
                                        inputProps={{ min: 0 }}
                                      />
                                    </Grid>
                                    <Grid item xs={6} md={4}>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        label="Max Duration (sec)"
                                        type="number"
                                        value={rangeWa.maxDuration}
                                        onChange={(e) =>
                                          setRangeWa((p) => ({
                                            ...p,
                                            maxDuration: e.target.value,
                                          }))
                                        }
                                        inputProps={{ min: 0 }}
                                      />
                                    </Grid>
                                    {rangeWa.templateId && (
                                      <Grid item xs={12}>
                                        <Chip
                                          size="small"
                                          label={`Template: ${
                                            waTemplates.find(
                                              (t) => t.id === rangeWa.templateId
                                            )?.name || rangeWa.templateId
                                          }`}
                                          color="success"
                                          variant="outlined"
                                          sx={{ mr: 1 }}
                                        />
                                        <Chip
                                          size="small"
                                          label={`${rangeWa.minDuration}s – ${rangeWa.maxDuration}s`}
                                          color="primary"
                                          variant="outlined"
                                        />
                                      </Grid>
                                    )}
                                  </Grid>
                                )}
                              </Paper>
                            </Grid>

                            {/* RCS range */}
                            <Grid item xs={12}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2.5,
                                  borderRadius: 2,
                                  bgcolor: rangeRcs.enabled
                                    ? "#f5f3ff"
                                    : "#fafafa",
                                }}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={2}
                                  mb={rangeRcs.enabled ? 2 : 0}
                                >
                                  <Message
                                    sx={{ color: "#6366f1", fontSize: 22 }}
                                  />
                                  <Typography fontWeight={500} sx={{ flex: 1 }}>
                                    RCS Fallback
                                  </Typography>
                                  <Switch
                                    size="small"
                                    checked={rangeRcs.enabled}
                                    onChange={(e) =>
                                      setRangeRcs((p) => ({
                                        ...p,
                                        enabled: e.target.checked,
                                      }))
                                    }
                                  />
                                </Stack>
                                {rangeRcs.enabled && (
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>RCS Template</InputLabel>
                                        <Select
                                          label="RCS Template"
                                          value={rangeRcs.templateId}
                                          onChange={(e) =>
                                            setRangeRcs((p) => ({
                                              ...p,
                                              templateId: e.target.value,
                                            }))
                                          }
                                        >
                                          <MenuItem value="">
                                            <em>Select template</em>
                                          </MenuItem>
                                          {(rcsTemplates || []).map((t) => (
                                            <MenuItem
                                              key={t.templateId}
                                              value={t.templateId}
                                            >
                                              {t.templateName}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    <Grid item xs={6} md={4}>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        label="Min Duration (sec)"
                                        type="number"
                                        value={rangeRcs.minDuration}
                                        onChange={(e) =>
                                          setRangeRcs((p) => ({
                                            ...p,
                                            minDuration: e.target.value,
                                          }))
                                        }
                                        inputProps={{ min: 0 }}
                                      />
                                    </Grid>
                                    <Grid item xs={6} md={4}>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        label="Max Duration (sec)"
                                        type="number"
                                        value={rangeRcs.maxDuration}
                                        onChange={(e) =>
                                          setRangeRcs((p) => ({
                                            ...p,
                                            maxDuration: e.target.value,
                                          }))
                                        }
                                        inputProps={{ min: 0 }}
                                      />
                                    </Grid>
                                    {rangeRcs.templateId && (
                                      <Grid item xs={12}>
                                        <Chip
                                          size="small"
                                          label={`Template: ${
                                            (rcsTemplates || []).find(
                                              (t) =>
                                                t.templateId ===
                                                rangeRcs.templateId
                                            )?.templateName ||
                                            rangeRcs.templateId
                                          }`}
                                          sx={{
                                            bgcolor: "#ede9fe",
                                            color: "#5b21b6",
                                          }}
                                        />
                                        <Chip
                                          size="small"
                                          label={`${rangeRcs.minDuration}s – ${rangeRcs.maxDuration}s`}
                                          color="primary"
                                          variant="outlined"
                                          sx={{ ml: 1 }}
                                        />
                                      </Grid>
                                    )}
                                  </Grid>
                                )}
                              </Paper>
                            </Grid>
                          </Grid>

                          <Divider sx={{ my: 3 }} />

                          {/* DTMF-Based */}
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            mb={0.5}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Sms fontSize="small" sx={{ color: "#f59e0b" }} />{" "}
                            DTMF-Based Fallback
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={3}
                          >
                            Send a message when the caller presses a specific
                            key.
                          </Typography>

                          <Stack spacing={2}>
                            {dtmfRows.map((row, idx) => (
                              <Paper
                                key={idx}
                                variant="outlined"
                                sx={{
                                  p: 2.5,
                                  borderRadius: 2,
                                  bgcolor:
                                    row.type === "whatsapp"
                                      ? "#f0fdf4"
                                      : "#f5f3ff",
                                  borderColor:
                                    row.type === "whatsapp"
                                      ? "#86efac"
                                      : "#c4b5fd",
                                }}
                              >
                                <Grid
                                  container
                                  spacing={2}
                                  alignItems="flex-start"
                                >
                                  <Grid item xs={6} sm={2}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Key</InputLabel>
                                      <Select
                                        label="Key"
                                        value={row.input}
                                        onChange={(e) =>
                                          updateDtmfRow(
                                            idx,
                                            "input",
                                            e.target.value
                                          )
                                        }
                                      >
                                        {DTMF_INPUTS.map((k) => (
                                          <MenuItem key={k} value={k}>
                                            {k}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={6} sm={2}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Channel</InputLabel>
                                      <Select
                                        label="Channel"
                                        value={row.type}
                                        onChange={(e) =>
                                          updateDtmfRow(
                                            idx,
                                            "type",
                                            e.target.value
                                          )
                                        }
                                      >
                                        <MenuItem value="whatsapp">
                                          <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                          >
                                            <WhatsApp
                                              sx={{
                                                fontSize: 16,
                                                color: "#25d366",
                                              }}
                                            />
                                            <span>WhatsApp</span>
                                          </Stack>
                                        </MenuItem>
                                        <MenuItem value="rcs">
                                          <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                          >
                                            <Message
                                              sx={{
                                                fontSize: 16,
                                                color: "#6366f1",
                                              }}
                                            />
                                            <span>RCS</span>
                                          </Stack>
                                        </MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} sm={3}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Template</InputLabel>
                                      <Select
                                        label="Template"
                                        value={row.templateId}
                                        onChange={(e) =>
                                          updateDtmfRow(
                                            idx,
                                            "templateId",
                                            e.target.value
                                          )
                                        }
                                      >
                                        <MenuItem value="">
                                          <em>Select</em>
                                        </MenuItem>
                                        {row.type === "whatsapp"
                                          ? waTemplates.map((t) => (
                                              <MenuItem key={t.id} value={t.id}>
                                                {t.name}{" "}
                                                <Typography
                                                  component="span"
                                                  variant="caption"
                                                  color="text.secondary"
                                                  ml={1}
                                                >
                                                  ({t.language})
                                                </Typography>
                                              </MenuItem>
                                            ))
                                          : (rcsTemplates || []).map((t) => (
                                              <MenuItem
                                                key={t.templateId}
                                                value={t.templateId}
                                              >
                                                {t.templateName}
                                              </MenuItem>
                                            ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} sm={3}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Condition</InputLabel>
                                      <Select
                                        label="Condition"
                                        value={row.condition}
                                        onChange={(e) =>
                                          updateDtmfRow(
                                            idx,
                                            "condition",
                                            e.target.value
                                          )
                                        }
                                      >
                                        <MenuItem value="">
                                          <em>Any outcome</em>
                                        </MenuItem>
                                        {WA_CONDITIONS.map((c) => (
                                          <MenuItem key={c} value={c}>
                                            {c.charAt(0).toUpperCase() +
                                              c.slice(1)}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={12}
                                    sm={2}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-end",
                                    }}
                                  >
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() => removeDtmfRow(idx)}
                                      disabled={dtmfRows.length === 1}
                                    >
                                      <DeleteOutlineIcon />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                                {row.templateId && (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    mt={1.5}
                                    flexWrap="wrap"
                                  >
                                    <Chip
                                      size="small"
                                      label={`Key: ${row.input}`}
                                      color="warning"
                                    />
                                    <Chip
                                      size="small"
                                      icon={
                                        row.type === "whatsapp" ? (
                                          <WhatsApp
                                            sx={{ fontSize: "14px !important" }}
                                          />
                                        ) : (
                                          <Message
                                            sx={{ fontSize: "14px !important" }}
                                          />
                                        )
                                      }
                                      label={
                                        row.type === "whatsapp"
                                          ? waTemplates.find(
                                              (t) => t.id === row.templateId
                                            )?.name || row.templateId
                                          : (rcsTemplates || []).find(
                                              (t) =>
                                                t.templateId === row.templateId
                                            )?.templateName || row.templateId
                                      }
                                      sx={{
                                        bgcolor:
                                          row.type === "whatsapp"
                                            ? "#dcfce7"
                                            : "#ede9fe",
                                        color:
                                          row.type === "whatsapp"
                                            ? "#166534"
                                            : "#5b21b6",
                                      }}
                                    />
                                    {row.condition && (
                                      <Chip
                                        size="small"
                                        label={`If: ${row.condition}`}
                                        color="default"
                                      />
                                    )}
                                  </Stack>
                                )}
                              </Paper>
                            ))}
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={addDtmfRow}
                              sx={{ alignSelf: "flex-start" }}
                            >
                              Add DTMF Rule
                            </Button>
                          </Stack>
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* ── Submit ─────────────────────────────────────────────── */}
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: "center", mt: 4 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || isUploadingContacts}
                        sx={{
                          px: 8,
                          py: 1.8,
                          fontSize: "1.1rem",
                          background:
                            "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                          },
                        }}
                        startIcon={
                          isSubmitting ? null : batchSettings.enabled ? (
                            <Preview />
                          ) : (
                            <Campaign />
                          )
                        }
                      >
                        {isSubmitting || isUploadingContacts ? (
                          <CircularProgress size={24} sx={{ color: "#fff" }} />
                        ) : batchSettings.enabled ? (
                          "📋 Preview Batch Schedule"
                        ) : (
                          "🚀 Launch Campaign Now"
                        )}
                      </Button>
                      {batchSettings.enabled && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          mt={1}
                        >
                          Contacts will be sent inline — no separate upload
                          needed
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grow>
      </Box>

      {/* ══════════════════════════════════════════════════════════════════════
          BATCH PREVIEW DIALOG  (fully implemented)
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={previewOpen}
        onClose={() => !isSubmitting && setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Schedule color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Batch Schedule Preview
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {previewData && (
            <>
              {/* Summary chips */}
              <Stack direction="row" spacing={1.5} flexWrap="wrap" mb={3}>
                <Chip
                  icon={<ContactPhone />}
                  label={`${previewData.totalContacts.toLocaleString()} total contacts`}
                  color="primary"
                />
                <Chip
                  icon={<Campaign />}
                  label={`${previewData.totalBatches} batches`}
                  color="secondary"
                />
                <Chip
                  icon={<Schedule />}
                  label={`${previewData.batchSize.toLocaleString()} per batch`}
                />
                <Chip
                  icon={<AccessTime />}
                  label={`${previewData.intervalMinutes} min interval`}
                />
              </Stack>

              {/* Blackout periods summary */}
              {batchSettings.nonActiveHours.length > 0 && (
                <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
                  <strong>Blackout windows applied:</strong>{" "}
                  {batchSettings.nonActiveHours.map((w, i) => (
                    <Chip
                      key={i}
                      size="small"
                      label={`${w.start} – ${w.end}`}
                      sx={{ ml: 0.5 }}
                    />
                  ))}{" "}
                  — batch times automatically skip these windows.
                </Alert>
              )}

              {/* Active days summary */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Active days:</strong>{" "}
                {batchSettings.activeDays
                  .sort()
                  .map((d) => DAY_LABELS[d])
                  .join(", ")}
              </Alert>

              {/* Schedule table */}
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Exact launch times (IST):
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  maxHeight: 420,
                  overflow: "auto",
                  bgcolor: "#fafafa",
                  borderRadius: 2,
                }}
              >
                <Stack spacing={0}>
                  {/* Header */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px 1fr 1fr",
                      px: 2,
                      py: 1,
                      bgcolor: "#f1f5f9",
                      borderBottom: "1px solid #e2e8f0",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                    >
                      BATCH
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                    >
                      CONTACTS
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                    >
                      SCHEDULED AT (IST)
                    </Typography>
                  </Box>

                  {previewData.schedules.map((sch, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "80px 1fr 1fr",
                        px: 2,
                        py: 1.5,
                        bgcolor: i % 2 === 0 ? "#fff" : "#f8fafc",
                        borderBottom: "1px solid #f1f5f9",
                        "&:last-child": { borderBottom: "none" },
                        transition: "background 0.15s",
                        "&:hover": { bgcolor: "#eff6ff" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Chip
                          size="small"
                          label={`#${sch.batch}`}
                          color="primary"
                          variant={i === 0 ? "filled" : "outlined"}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        {sch.contacts.toLocaleString()}
                        {i === 0 && (
                          <Chip
                            size="small"
                            label="First"
                            color="success"
                            sx={{ ml: 1, height: 18, fontSize: "0.65rem" }}
                          />
                        )}
                        {i === previewData.schedules.length - 1 && (
                          <Chip
                            size="small"
                            label="Last"
                            color="warning"
                            sx={{ ml: 1, height: 18, fontSize: "0.65rem" }}
                          />
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontWeight={600}
                        color="text.primary"
                      >
                        {sch.scheduledAt}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>

              <Alert severity="success" sx={{ mt: 2 }}>
                Campaign will finish around{" "}
                <strong>
                  {
                    previewData.schedules[previewData.schedules.length - 1]
                      ?.scheduledAt
                  }
                </strong>
                . Contacts are sent inline — no separate file upload.
              </Alert>
            </>
          )}
          {previewLoading && (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <CircularProgress />
              <Typography mt={2}>Calculating batch schedule…</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setPreviewOpen(false)} disabled={isSubmitting}>
            ← Go Back &amp; Edit
          </Button>
          <Button
            variant="contained"
            disabled={isSubmitting}
            onClick={handlePreviewConfirm}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                <Campaign />
              )
            }
            sx={{
              px: 4,
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
              },
            }}
          >
            {isSubmitting ? "Launching…" : "✅ Confirm & Launch Campaign"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── SUCCESS DIALOG ──────────────────────────────────────────────────── */}
      <Dialog
        open={successDialog.open}
        onClose={() =>
          setSuccessDialog({ open: false, campaignId: "", message: "" })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", pt: 4 }}>
          <SuccessIcon sx={{ fontSize: 80, color: "#10b981", mb: 2 }} />
          <Typography variant="h4" fontWeight={700} color="#10b981">
            Campaign Launched!
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          <Typography variant="h6" gutterBottom>
            {successDialog.message}
          </Typography>
          <Box
            sx={{
              mt: 3,
              p: 3,
              background: "#f0fdf4",
              borderRadius: 3,
              display: "inline-block",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              CAMPAIGN ID
            </Typography>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ fontFamily: "monospace", letterSpacing: 2 }}
            >
              {successDialog.campaignId}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 4 }}>
          <Button
            variant="contained"
            onClick={() => {
              setSuccessDialog({ open: false, campaignId: "", message: "" });
              window.location.reload();
            }}
            sx={{ px: 6, py: 1.5 }}
          >
            Create Another Campaign
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── CREATE AGENT GROUP DIALOG ────────────────────────────────────────── */}
      <Dialog
        open={createGroupDialog}
        onClose={() => !isCreatingGroup && setCreateGroupDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Agent Group</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Group Name"
              fullWidth
              required
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
            />
            <Typography variant="subtitle2">Agents</Typography>
            {newGroupAgents.map((agent, idx) => (
              <Paper key={idx} sx={{ p: 2, bgcolor: "#f9f9f9" }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={5}>
                    <TextField
                      label="Agent Number"
                      fullWidth
                      size="small"
                      required
                      value={agent.agentNumber}
                      onChange={(e) => {
                        const u = [...newGroupAgents];
                        u[idx].agentNumber = e.target.value;
                        setNewGroupAgents(u);
                      }}
                      inputProps={{ maxLength: 10 }}
                      helperText="10-digit number"
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      label="Agent Name"
                      fullWidth
                      size="small"
                      value={agent.agentName}
                      onChange={(e) => {
                        const u = [...newGroupAgents];
                        u[idx].agentName = e.target.value;
                        setNewGroupAgents(u);
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      color="error"
                      onClick={() =>
                        setNewGroupAgents(
                          newGroupAgents.filter((_, i) => i !== idx)
                        )
                      }
                      disabled={newGroupAgents.length === 1}
                    >
                      <Close />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() =>
                setNewGroupAgents([
                  ...newGroupAgents,
                  { agentNumber: "", agentName: "" },
                ])
              }
            >
              Add Another Agent
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateGroupDialog(false)}
            disabled={isCreatingGroup}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              !newGroupName.trim() ||
              newGroupAgents.some((a) => !a.agentNumber.trim()) ||
              isCreatingGroup
            }
            onClick={handleCreateGroup}
          >
            {isCreatingGroup ? <CircularProgress size={24} /> : "Create Group"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── VIEW GROUP DIALOG ───────────────────────────────────────────────── */}
      <Dialog
        open={viewGroupDialogOpen}
        onClose={() => setViewGroupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <Info color="primary" />
            Group Details: {viewingGroup?.groupName}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {viewingGroup?.agents?.length > 0 ? (
            <Stack spacing={2}>
              {viewingGroup.agents.map((agent, idx) => (
                <Paper key={idx} sx={{ p: 2, bgcolor: "#f8fafc" }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {agent.agentName || "Unnamed Agent"}
                  </Typography>
                  <Typography color="primary" sx={{ fontFamily: "monospace" }}>
                    {agent.agentNumber}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">
              No agents in this group
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setViewGroupDialogOpen(false)}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── SAVE TO LIST DIALOG (non-batch path) ─────────────────────────────── */}
      <Dialog
        open={saveToListDialog}
        onClose={() => !isUploadingContacts && setSaveToListDialog(false)}
      >
        <DialogTitle>Save Contact List?</DialogTitle>
        <DialogContent>
          <Typography>
            {pendingContacts.length.toLocaleString()} valid contacts found
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Would you like to save this list for future campaigns?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSaveToListDialog(false)}
            disabled={isUploadingContacts}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleSaveToListConfirm(false)}
            variant="outlined"
            disabled={isUploadingContacts}
          >
            No, use once
          </Button>
          <Button
            onClick={() => handleSaveToListConfirm(true)}
            variant="contained"
            disabled={isUploadingContacts}
          >
            Yes, Save List
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── SNACKBAR ────────────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Re-export DAY_LABELS so it's accessible in the preview dialog
const DAY_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export default ImprovedVoiceCampaignForm;
